import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { Collapse, Divider, Grid } from "@mui/material";
import clsx from "clsx";
import { IPerformanceTable, IAdvisorPerformanceTable } from "@/Washouts/types/advisors";
import { DEFAULT_COLLAPSED_SIZE } from "@/Washouts/constants";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import { GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
import { useEffect, useMemo, useState } from "react";
import { errorLogger, getError } from "@/Common/helpers/logger";
import useAggregationExtended from "@/Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";
import { nullishResolver } from "@/Common/helpers/sonar";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { getColumnDefinitions } from "./columnsDefinition";

const resetApiFilter = (apiRef: ReturnType<typeof useGridApiRef>) => {
	try {
		const { current } = apiRef;
		if (current?.state?.filter) {
			const cleanFilter = {
				filterModel: { items: [], linkOperator: "and" },
				visibleRows: null,
				visibleRowsLookup: {},
				filteredRowsLookup: {},
				filteredDescendantCountLookup: {},
			};

			current.setState((prevState) => ({
				...prevState,
				filter: cleanFilter,
			}));
		}
	} catch (error) {
		errorLogger(getError(error));
	}
};

const getRowModels = (apiRef: ReturnType<typeof useGridApiRef>) => apiRef?.current?.getRowModels?.() ?? new Map();

const resolveRowsFromRowModels = (rowsAsMap: Map<unknown, GridValidRowModel>) => {
	return [...rowsAsMap.values()];
};

const shouldShowUnfilteredData = (apiRef: ReturnType<typeof useGridApiRef>) => !apiRef?.current?.state?.filter?.filterModel?.items?.length;

const flattenFooterModels = <T extends Record<string, unknown>>(footerModels: T): Record<string, string> => {
	const result: Record<string, string> = {};

	const flatten = (obj: Record<string, unknown>, parentKey = ""): void => {
		Object.entries(obj).forEach(([key, value]) => {
			const newKey = parentKey ? `${parentKey}.${key}` : key;
			if (typeof value === "object" && value !== null) {
				flatten(value as Record<string, unknown>, newKey);
			} else {
				result[newKey] = String(value);
			}
		});
	};

	flatten(footerModels);
	return result;
};

const CustomDivider = ({ collapsed, sxSectionDivider }: { collapsed: boolean; sxSectionDivider?: object }) => (
	<Divider
		className={clsx("!mx-[24px] !border-b !border-[#CCC]", {
			"!py-[5px] !mt-[24px]": !sxSectionDivider && !collapsed,
			"!pt-0": !sxSectionDivider && collapsed,
		})}
		sx={sxSectionDivider}
	/>
);

function PerformanceTable(props: Readonly<IPerformanceTable>) {
	const [collapsed, setCollapsed] = useState(true);
	const apiRef = useGridApiRef();
	const columns = useMemo(() => getColumnDefinitions(), [apiRef]);
	const [rows, setRows] = useState<GridValidRowModel[]>([]);

	const aggregations = useAggregationExtended({
		selectedSum: {},
	});

	// Define state for column visibility
	const [showColumns, setShowColumns] = useState(false);
	// Define columns that can be collapsed (percentage and hours)
	const collapsibleColumns = columns.filter((col) => /percentage|hours/i.test(col.field)).map((col) => col.field);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState(
		collapsibleColumns.reduce((model, col) => ({ ...model, [col]: false }), { _id: false })
	);

	useEffect(() => {
		// Clear filter on purpose
		resetApiFilter(apiRef);
		setCollapsed(Boolean(props?.collapsed));

		const rowsAsMap = getRowModels(apiRef);
		const resolvedRows = resolveRowsFromRowModels(rowsAsMap);

		setRows(shouldShowUnfilteredData(apiRef) ? nullishResolver(props?.data, []) : resolvedRows);

		return () => {
			setCollapsed(true);
		};
	}, [props, apiRef]);

	const showTableConditions = Boolean(apiRef);
	if (!showTableConditions) {
		return null;
	}

	const footerModels = {
		customerPay: {
			labor: {
				amount: "sum",
				hours: "sum",
			},
			parts: {
				amount: "sum",
			},
		},
		warranty: {
			labor: {
				amount: "sum",
				hours: "sum",
			},
			parts: {
				amount: "sum",
			},
		},
		internal: {
			labor: {
				amount: "sum",
				hours: "sum",
			},
			parts: {
				amount: "sum",
			},
		},
		commission: {
			labor: "sum",
			parts: "sum",
			total: "sum",
		},
		subletGross: "sum",
		discountAmount: "sum",
		totalGross: "sum",
		repairOrderCount: "repairOrderCountAggregation",
	};

	const footerModelsFlat = flattenFooterModels(footerModels);

	return (
		<>
			<Collapse in={!collapsed} collapsedSize={DEFAULT_COLLAPSED_SIZE} timeout={100}>
				{showTableConditions ? (
					<DataSmartTable
						{...props.tableProps}
						apiRef={apiRef}
						columns={columns}
						rows={rows ?? []}
						hideFooter
						disableColumnMenu
						disableRowSelectionOnClick
						disableMultipleRowSelection
						checkboxSelection={false}
						columnVisibilityModel={columnVisibilityModel}
						initialState={{
							aggregation: {
								model: footerModelsFlat,
							},
						}}
						slots={{
							toolbar: CommonToolbar,
							noRowsOverlay: NoRowsOverlay,
						}}
						slotProps={{
							toolbar: {
								titleExpandible: {
									collapsed,
									hide: false,
									collapsible: true,
									title: props?.title,
									titleClasses: "page-title",
									sxContainer: { padding: "20px 32px" },
									onClick: setCollapsed,
								},
								filter: {
									hide: false,
								},
								search: {
									useQuickFilter: true,
								},
								expand: {
									setColumnVisibilityModel,
									hide: false,
									expanded: showColumns,
									columnsCollapsible: collapsibleColumns,
									onExpandAction: (expanded: boolean) => {
										setShowColumns(expanded);
									},
								},
							},
							noRowsOverlay: {
								title: "No Performance Data",
							},
						}}
						customProps={{
							tableClassName: {
								"[&_.MuiDataGrid-main]:mx-[30px] [&_.MuiDataGrid-main]:w-[1340px]": true,
								"[&_.toolbar-actions]:absolute [&_.toolbar-actions]:top-[15px] [&_.toolbar-actions]:right-[30px]": true,
								// Improve overlay look when no rows after filtering
								"[&_.MuiDataGrid-overlay]:font-montserrat": true,
								"[&_.MuiDataGrid-overlay]:text-[16px]": true,
								"[&_.MuiDataGrid-overlay]:text-[color:--text-color]": true,
							},
							customAggregationsFn: { ...aggregations },
						}}
					/>
				) : null}
			</Collapse>
			<Collapse in={Boolean(props.divider)} collapsedSize={0} timeout={100}>
				<CustomDivider collapsed={collapsed} sxSectionDivider={props.sxSectionDivider} />
			</Collapse>
		</>
	);
}

export default function AdvisorPerformanceTable(props: Readonly<IAdvisorPerformanceTable>) {
	if (props.loading) {
		return (
			<>
				<Grid className="py-[20px] px-[32px]">
					<TableLoader smallTitle toolbar />
				</Grid>
				{props.divider && <CustomDivider collapsed={false} sxSectionDivider={props.sxSectionDivider} />}
			</>
		);
	}

	return <>{props.dataPresent && <PerformanceTable {...props} divider />}</>;
}
